import React, { useState, useEffect } from 'react';

const BASE_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/resource';

function getAuthHeaders() {
  const token = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('access_token'))
    || (typeof localStorage !== 'undefined' && localStorage.getItem('access_token'));
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const apiRequest = async (endpoint, method = 'GET', data = null) => {
  const url = `${BASE_URL}${endpoint}`;
  
  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(), 
    },
  };

  if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
    options.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(url, options);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

function FeaturesTab({ resource, onUpdate }) {
  const [features, setFeatures] = useState({});
  const [amenities, setAmenities] = useState({});
  const [security, setSecurity] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadFeaturesData = async () => {
      try {
        setIsLoading(true);
        console.log('Loading features data for resource:', resource);
        
        // Get mongo_id from resource
        const mongoId = resource.mongo_id;
        console.log('Using mongo_id:', mongoId);
        
        if (!mongoId) {
          console.error('No mongo_id found in resource');
          // Initialize with empty objects
          setFeatures({});
          setAmenities({});
          setSecurity({});
          setIsLoading(false);
          return;
        }
        
        // Fetch MongoDB data
        const mongoData = await apiRequest(`/getMongoDbResourceData/${mongoId}`, 'GET');
        console.log('Fetched MongoDB data:', mongoData);
        
        // Extract features, amenities, security
        const featuresObj = {};
        const amenitiesObj = {};
        const securityObj = {};

        // Features
        Object.entries(mongoData.features || {}).forEach(([key, value]) => {
          featuresObj[key] = typeof value === 'object' ? value.enabled : value;
        });

        // Amenities
        Object.entries(mongoData.amenities || {}).forEach(([key, value]) => {
          amenitiesObj[key] = typeof value === 'object' ? value.enabled : value;
        });

        // Security
        Object.entries(mongoData.security || {}).forEach(([key, value]) => {
          securityObj[key] = typeof value === 'object' ? value.enabled : value;
        });

        console.log('Processed features:', featuresObj);
        console.log('Processed amenities:', amenitiesObj);
        console.log('Processed security:', securityObj);

        setFeatures(featuresObj);
        setAmenities(amenitiesObj);
        setSecurity(securityObj);
      } catch (error) {
        console.error('Error loading features data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadFeaturesData();
  }, [resource]);

  const handleItemToggle = (category, itemId, value) => {
    if (category === 'features') {
      setFeatures(prev => ({ ...prev, [itemId]: value }));
    } else if (category === 'amenities') {
      setAmenities(prev => ({ ...prev, [itemId]: value }));
    } else if (category === 'security') {
      setSecurity(prev => ({ ...prev, [itemId]: value }));
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Use the Hapio resource ID, NOT mongo_id
      const resourceId = resource.id;
      
      if (!resourceId) {
        throw new Error('Resource ID not found in resource data');
      }
  
      // Format the data according to the PATCH /updateMongo/:resource_id spec
      const requestBody = {
        features: Object.entries(features).reduce((acc, [key, value]) => {
          acc[key] = { enabled: value };
          return acc;
        }, {}),
        amenities: Object.entries(amenities).reduce((acc, [key, value]) => {
          acc[key] = { enabled: value };
          return acc;
        }, {}),
        security: Object.entries(security).reduce((acc, [key, value]) => {
          acc[key] = { enabled: value };
          return acc;
        }, {})
      };
  
      console.log('Updating features with resource ID:', resourceId);
      console.log('Request body:', requestBody);
      
      // Use PATCH to /updateMongo/:resource_id (where resource_id is the Hapio ID)
      const response = await apiRequest(`/updateMongoDbResourceData/${resourceId}`, 'PATCH', requestBody);
      
      console.log('Update response:', response);
      
      if (response.message === 'Resource updated successfully') {
        const successMessage = document.createElement('div');
        successMessage.className = 'fixed top-4 right-4 bg-[#79C485] text-white px-6 py-3 rounded-lg shadow-lg z-50';
        successMessage.textContent = 'Features updated successfully!';
        document.body.appendChild(successMessage);
        
        setTimeout(() => {
          if (successMessage.parentNode) {
            successMessage.parentNode.removeChild(successMessage);
          }
        }, 3000);
        
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Error updating features:', error);
      alert('Failed to update features: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const CategorySection = ({ title, items, category }) => (
    <div>
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      <div className="grid grid-cols-3 gap-4">
        {Object.keys(items).length === 0 ? (
          <div className="col-span-3 text-center py-8">
            <p className="text-gray-500 text-sm italic">No {title.toLowerCase()} available</p>
          </div>
        ) : (
          Object.entries(items).map(([key, value]) => (
            <div key={key}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={value}
                  onChange={(e) => handleItemToggle(category, key, e.target.checked)}
                  className="w-4 h-4 text-blue-600 cursor-pointer" 
                />
                <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
              </label>
            </div>
          ))
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading features...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <CategorySection title="Features" items={features} category="features" />
      <CategorySection title="Amenities" items={amenities} category="amenities" />
      <CategorySection title="Security" items={security} category="security" />

      <div className="flex justify-end pt-4 border-t border-gray-200">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSaving && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

export default FeaturesTab;